/**
 * Enter Credentials Step (Step 2)
 * Input credentials based on platform auth method
 */

import React, { useState } from 'react';
import { Eye, EyeOff, CheckCircle, XCircle, Loader2, ExternalLink } from 'lucide-react';
import { useTestConnection } from '../../../hooks/useNetworksData';
import type { Step1Data, Step2Data } from '../../../types/networks.types';
import { toast } from 'sonner';

interface EnterCredentialsProps {
  platformData: Step1Data;
  data: Step2Data;
  onChange: (data: Step2Data) => void;
}

export const EnterCredentials: React.FC<EnterCredentialsProps> = ({
  platformData,
  data,
  onChange
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const testMutation = useTestConnection();
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleCredentialChange = (key: string, value: string) => {
    onChange({
      ...data,
      credentials: {
        ...data.credentials,
        [key]: value
      }
    });
  };

  const handleTestConnection = async () => {
    try {
      const result = await testMutation.mutateAsync(data);
      setTestResult(result);
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      setTestResult({ success: false, message: 'Connection test failed' });
      toast.error('Connection test failed');
    }
  };

  const isFormValid = () => {
    if (!data.login_url) return false;
    
    if (platformData.auth_method === 'username_password') {
      return data.credentials.username && data.credentials.password;
    } else if (platformData.auth_method === 'api_key') {
      const hasApiKey = data.credentials.api_key;
      const needsSecret = platformData.required_credentials?.includes('api_secret');
      return hasApiKey && (!needsSecret || data.credentials.api_secret);
    }
    
    return false;
  };

  return (
    <div className="space-y-6">
      {/* Platform Info */}
      <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
        {platformData.platform_logo && (
          <img
            src={platformData.platform_logo}
            alt={platformData.platform_name}
            className="h-10 object-contain"
          />
        )}
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
            {platformData.platform_name}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Authentication: {platformData.auth_method?.replace('_', ' ')}
          </p>
        </div>
      </div>

      {/* Login URL */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Login URL <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="url"
            value={data.login_url}
            onChange={(e) => onChange({ ...data, login_url: e.target.value })}
            placeholder="https://platform.com/login"
            className="w-full px-4 py-2 pr-10 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            required
          />
          {data.login_url && (
            <a
              href={data.login_url}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-blue-600"
              aria-label="Open login URL"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
          The URL where you log in to this platform
        </p>
      </div>

      {/* Username/Password Fields */}
      {platformData.auth_method === 'username_password' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Username <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.credentials.username || ''}
              onChange={(e) => handleCredentialChange('username', e.target.value)}
              placeholder="Enter your username"
              className="w-full px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              required
              autoComplete="username"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={data.credentials.password || ''}
                onChange={(e) => handleCredentialChange('password', e.target.value)}
                placeholder="Enter your password"
                className="w-full px-4 py-2 pr-10 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Optional XML Feed API Key for daily data */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              XML API Key <span className="text-gray-400 text-xs">(Optional - for daily stats)</span>
            </label>
            <input
              type="text"
              value={data.credentials.api_key || ''}
              onChange={(e) => handleCredentialChange('api_key', e.target.value)}
              placeholder="From XML Feeds section in affiliate portal"
              className="w-full px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Adding the XML API key enables daily statistics. Find it in the affiliate portal under "XML Feeds".
            </p>
          </div>
        </>
      )}

      {/* API Key Fields */}
      {platformData.auth_method === 'api_key' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              API Key <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.credentials.api_key || ''}
              onChange={(e) => handleCredentialChange('api_key', e.target.value)}
              placeholder="Enter your API key"
              className="w-full px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              required
            />
          </div>

          {platformData.required_credentials?.includes('api_secret') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                API Secret <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showApiSecret ? 'text' : 'password'}
                  value={data.credentials.api_secret || ''}
                  onChange={(e) => handleCredentialChange('api_secret', e.target.value)}
                  placeholder="Enter your API secret"
                  className="w-full px-4 py-2 pr-10 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowApiSecret(!showApiSecret)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  aria-label={showApiSecret ? 'Hide API secret' : 'Show API secret'}
                >
                  {showApiSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* OAuth */}
      {platformData.auth_method === 'oauth' && (
        <div className="p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-center">
          <p className="text-sm text-blue-900 dark:text-blue-300 mb-4">
            This platform uses OAuth authentication. Click the button below to authorize access.
          </p>
          <button
            onClick={() => toast.info('OAuth integration coming soon')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Connect with OAuth
          </button>
        </div>
      )}

      {/* Test Connection Button */}
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={handleTestConnection}
          disabled={!isFormValid() || testMutation.isPending}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {testMutation.isPending ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Testing Connection...
            </>
          ) : (
            'Test Connection'
          )}
        </button>
      </div>

      {/* Test Result */}
      {testResult && (
        <div className={`p-4 rounded-lg border ${
          testResult.success
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
        }`}>
          <div className="flex items-start gap-3">
            {testResult.success ? (
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
            ) : (
              <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            )}
            <div>
              <p className={`text-sm font-medium ${
                testResult.success
                  ? 'text-green-900 dark:text-green-300'
                  : 'text-red-900 dark:text-red-300'
              }`}>
                {testResult.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <strong>Security Note:</strong> Your credentials are encrypted and stored securely. We never share your login information with third parties.
        </p>
      </div>
    </div>
  );
};
